import dayjs from "dayjs";
import "dayjs/locale/ru";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Platforma vaqt zonasi.
 *
 * `.local()` ISHLATILMAYDI: u BRAUZERNING zonasini oladi, ya'ni
 * Moskvadagi moderator uchrashuv vaqtini bir soat oldin, Istanbuldagisi
 * ikki soat oldin ko'rardi — ayni paytda dashboard va backend kunni
 * Toshkent bo'yicha kesadi. Bitta panelda ikki xil vaqt bo'lishi
 * nizoni ko'rib chiqishni imkonsiz qiladi.
 */
export const APP_TIMEZONE = "Asia/Tashkent";

/**
 * Backend ISO 8601 ni UTC da qaytaradi (backend `docs/CONVENTIONS.md`).
 * Admin panel Toshkent vaqtida ishlaydi — konvertatsiya shu yerda, bir joyda.
 */
export const formatDateTime = (iso: string | null | undefined): string =>
  iso ? dayjs.utc(iso).tz(APP_TIMEZONE).format("DD.MM.YYYY HH:mm") : "—";

export const formatDate = (iso: string | null | undefined): string =>
  iso ? dayjs.utc(iso).tz(APP_TIMEZONE).format("DD.MM.YYYY") : "—";

/**
 * O'zbekcha qisqa oy nomlari QO'LDA yozilgan: brauzerlarning bir qismida
 * `uz-UZ` uchun sana ma'lumotlari yo'q va Intl root lokalga tushib
 * "M08 31" kabi natija beradi. Shu sababli uz uchun Intl ga tayanmaymiz.
 */
const UZ_SHORT_MONTHS = [
  "yan",
  "fev",
  "mar",
  "apr",
  "may",
  "iyn",
  "iyl",
  "avg",
  "sen",
  "okt",
  "noy",
  "dek",
];

/**
 * Grafik o'qi uchun qisqa sana: "30 avg".
 *
 * `formatDate` dan farqi — u to'liq sana beradi va o'qda sig'maydi;
 * bu yerda esa faqat kun va oy kerak.
 */
export const formatDayMonth = (date: string, locale = "uz"): string => {
  // "2026-08-30" ni Date ga bersak, u UTC yarim tuni sifatida o'qiladi va
  // manfiy zonalarda kun bir kunga siljiydi — shuning uchun qo'lda ajratamiz.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const parsed = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(date);

  if (Number.isNaN(parsed.getTime())) return "—";

  if (locale === "en" || locale === "ru") {
    return new Intl.DateTimeFormat(locale === "en" ? "en-US" : "ru-RU", {
      day: "numeric",
      month: "short",
    }).format(parsed);
  }

  return `${parsed.getDate()} ${UZ_SHORT_MONTHS[parsed.getMonth()]}`;
};

/** "3 soat oldin" — ro'yxatlarda aniq vaqtdan ko'ra tezroq o'qiladi. */
export const formatRelative = (iso: string | null | undefined, locale = "uz"): string => {
  if (!iso) return "—";
  // dayjs da o'zbek lokali yo'q; ruscha shakl kirill o'qiydigan foydalanuvchi
  // uchun to'g'ri, qolganlarga inglizcha qoladi.
  return dayjs
    .utc(iso)
    .locale(locale === "ru" ? "ru" : "en")
    .fromNow();
};

/** 0–1 → "42%". Backend ulushni kasr son sifatida qaytaradi. */
export const formatPercent = (ratio: number, locale = "uz"): string =>
  new Intl.NumberFormat(locale === "en" ? "en-US" : "ru-RU", {
    style: "percent",
    maximumFractionDigits: 1,
  }).format(ratio);

export const formatNumber = (value: number, locale = "uz"): string =>
  new Intl.NumberFormat(locale === "en" ? "en-US" : "ru-RU").format(value);
